import ProductAddToBasket from '@/components/Product/ProductAddToBasket'
import OfferSubscription from '@/components/Subscription/OfferSubscription'
import isStoreBagLine from '@/lib/basket/isStoreBagLine'
import havePremiumPrice from '@/lib/products/havePremiumPrice'
import * as React from 'react'
import { useState } from 'react'
import { ClickAwayListener, useMediaQuery } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'
import ProductQtyDropdown from './ProductQtyDropdown'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ProductQtyRow from './ProductQtyRow'
import generatePriceOptions from '@/lib/products/generatePriceOptions'
import findCompatiblePriceOption from '@/lib/products/findCompatiblePriceOption'
import PREMIUM from '@/src/PREMIUM'
import clsx from 'clsx'
import { ProductsBasket } from '@/type/basket'
import { Product } from '@/src/types/api-types'

export type ProductQtyInputStyles = {
  inputContainer?: React.CSSProperties
  textField?: React.CSSProperties
  textFieldInput?: React.CSSProperties
  dropdownButton?: React.CSSProperties
  dropdownButtonText?: React.CSSProperties
  dropdownIcon?: React.CSSProperties
  cellContainer?: React.CSSProperties
  cellQtyText?: React.CSSProperties
  rowContainer?: React.CSSProperties
  rowCell?: React.CSSProperties
}

// Quantite initiale telle qu'affichee par le selecteur : 1 PCB minimum (ou min_qty).
// A utiliser par les cartes produit pour initialiser leur etat qtyCart, afin que le
// bouton "Ajouter au panier" soit actif avec la quantite reellement visible.
export const getInitialDisplayedQty = (
  product: Product | ProductsBasket,
  initialQty = 1
): number => {
  const minQty = product?.min_qty || 1
  const coeffQty = Number(product?.coeffQuantity) || 1

  return initialQty === 1 ? Math.max(coeffQty, minQty) : initialQty
}

export const defaultFormatPrice = (n: number) => {
  const value = Number(n)
  // Garde-fou : ne jamais afficher "NaN € HT" quand le prix est absent
  if (!Number.isFinite(value)) return <span>—</span>

  return (
    <span>
      {value.toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
      &nbsp;€&nbsp;<sup className="font-semibold">HT</sup>
    </span>
  )
}

interface Props {
  product: Product | ProductsBasket
  onChange?: (qty: number) => void
  initialQty?: number
  dropdownMaxHeight?: number | string
  styles?: ProductQtyInputStyles
  displayMode?: 'row' | 'input'
  showAddButton?: boolean
  showInfoBar?: boolean
  compact?: boolean
  classes?: Record<string, string>
}

const ProductQtyInput: React.FC<Props> = ({
  product,
  onChange,
  initialQty = 1,
  dropdownMaxHeight = 280,
  styles = {},
  displayMode = 'row',
  showAddButton = false,
  showInfoBar = true,
  compact = false,
  classes = {},
}) => {
  const minQty = product.min_qty || 1
  const [qty, setQty] = React.useState<number>(getInitialDisplayedQty(product, initialQty))
  const [qtyInput, setQtyInput] = useState<number>(qty)
  const [open, setOpen] = React.useState(false)
  const btnRef = React.useRef<HTMLDivElement | null>(null)
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [showPremiumDialog, setShowPremiumDialog] = useState<boolean>(false)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Le prix doit rester visible en mobile : on garde la barre prix + quantite
  // partout, seul l'infoBar (badges premium/degressif) est masque en mobile
  const formatMode = displayMode

  const dropdownWidth = containerRef?.current?.offsetWidth

  const stepPriceOptions = generatePriceOptions(product)
  const currentPriceOption = qty ? findCompatiblePriceOption(stepPriceOptions, qty) : undefined
  const bestPriceOption = stepPriceOptions[stepPriceOptions.length - 1]
  const maxPremiumDiscount = product?.prices_current_subscription?.reduce((max, price) => {
    return Math.max(max, price.percent_discount)
  }, 0)

  const isReadOnly = isStoreBagLine(product as ProductsBasket)

  const handleQuantityChange = (newQty: number, close = true) => {
    const secureQty = ensurePcbMultiple(Number(newQty))
    if (isReadOnly) return
    if (secureQty === qty) return

    setQty(secureQty)
    setQtyInput(secureQty)
    onChange?.(secureQty)
    if (close) setOpen(false)
  }

  // Valeur PCB (pas de contrainte si absent ou <= 1)
  const pcbValue = product?.coeffQuantity ? parseFloat(product.coeffQuantity.toString()) : 1

  const ensurePcbMultiple = (val: number): number => {
    if (!pcbValue || pcbValue <= 1) return val

    const minQuantity = product.min_qty || 1

    const modulo = val % pcbValue
    if (modulo === 0) return Math.max(Math.max(pcbValue, val), minQuantity)

    const value = Math.max(pcbValue, val + (pcbValue - modulo))

    return Math.max(value, minQuantity)
  }

  const classesQtyButton = `inline-flex h-5 w-5 items-center justify-center rounded-full bg-white hover:bg-slate-50`

  const renderInput = () => (
    <div>
      {open ? (
        <div className="flex items-center rounded-md bg-[#F5F9FE] p-1">
          <div
            title="Diminuer"
            className={clsx(classesQtyButton, isReadOnly && 'opacity-50 cursor-not-allowed')}
            onClick={() => {
              if (isReadOnly) return
              const next = Math.max(pcbValue || 1, (qty || 0) - (pcbValue || 1))
              handleQuantityChange(next, false)
            }}
          >
            −
          </div>
          <input
            className="w-12 bg-transparent border-0 text-center text-[#949696] font-semibold focus:outline-none"
            type="text"
            inputMode="numeric"
            autoFocus
            value={qtyInput}
            onChange={e => {
              setQtyInput(Number(e.target.value))
            }}
            onKeyDown={event => {
              if (event.code === 'Enter') handleQuantityChange(qtyInput, false)
            }}
            onBlur={() => handleQuantityChange(qtyInput, false)}
            readOnly={isReadOnly}
          />
          <div
            title="Augmenter"
            className={clsx(classesQtyButton, isReadOnly && 'opacity-50 cursor-not-allowed')}
            onClick={() => {
              if (isReadOnly) return
              const next = (qty || 0) + (pcbValue || 1)
              handleQuantityChange(next, false)
            }}
          >
            +
          </div>
        </div>
      ) : (
        <div
          id={`qty-trigger-${product.product_id}`}
          ref={btnRef}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          className={clsx(
            'select-none flex items-center justify-center text-[#949696] font-semibold',
            isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          )}
          onClick={() => {
            if (isReadOnly) return
            setOpen(true)
          }}
          tabIndex={isReadOnly ? -1 : 0}
          title="Quantité"
        >
          <span>{qty != 0 ? qty : '—'}</span>
          {!isReadOnly && <ArrowDropDownIcon />}
        </div>
      )}
    </div>
  )

  const renderBar = () => (
    <div
      className={`flex justify-between items-center min-w-[210px] sm:min-w-[250px] max-w-[600px] w-full rounded-md cursor-pointer`}
      style={styles.rowContainer}
      ref={containerRef}
    >
      <ProductQtyRow
        opt={{
          originalPrice: currentPriceOption?.originalPrice ?? bestPriceOption?.originalPrice,
          discount: currentPriceOption?.discount ?? bestPriceOption?.discount,
          finalPrice: currentPriceOption?.finalPrice ?? bestPriceOption?.finalPrice,
          quantity: qty,
        }}
        classes={{
          discountContainer: 'bg-[#FFF2D4]',
          ...classes,
        }}
        onClick={() => {
          if (isReadOnly) return
          setOpen(true)
        }}
        rightSlot={renderInput()}
        showBadge={bestPriceOption?.quantity <= qty && stepPriceOptions?.length > 1}
        showBorder={true}
        showShadow={true}
        compact={compact}
      />
    </div>
  )

  const content = formatMode === 'input' ? renderInput() : renderBar()

  const infoBar = showInfoBar && !isMobile && (
    <div className={'w-full flex justify-between px-4 h-4'}>
      {havePremiumPrice(product) ? (
        <div
          className={
            'bg-[linear-gradient(90deg,#FF9214_0%,#FFCF6A_100%)] rounded-lg text-xs text-white px-2 font-semibold'
          }
        >
          -{Math.round(maxPremiumDiscount)}% avec {PREMIUM.name}
        </div>
      ) : (
        <div />
      )}

      {stepPriceOptions.length > 1 ? (
        <div className={'bg-[#FFF2D4] text-[#FFAC0A] rounded-lg text-xs px-2 font-semibold'}>
          Prix dégressif -{Math.round(bestPriceOption.discount)}%
        </div>
      ) : (
        <div />
      )}
    </div>
  )

  const shouldShowDropdown = open && (stepPriceOptions.length > 1 || havePremiumPrice(product))

  const tooltipNode = shouldShowDropdown ? (
    <Tooltip
      title={
        <ProductQtyDropdown
          product={product}
          onChange={handleQuantityChange}
          initialQty={qty}
          onClose={() => setOpen(false)}
          maxHeight={dropdownMaxHeight}
          currentQty={qty}
          classes={{
            footer: 'bg-white',
            ...classes,
          }}
          width={dropdownWidth}
          onClickPremium={() => setShowPremiumDialog(true)}
          compact={compact}
        />
      }
      placement={'bottom-start'}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      open={true}
      arrow={false}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: () => {
                return [0, 2]
              },
            },
          },
        ],
      }}
      slotProps={{
        tooltip: {
          className: '!bg-transparent !m-0 !p-0',
        },
      }}
    >
      {content}
    </Tooltip>
  ) : (
    content
  )

  return (
    <>
      <div className={clsx('flex gap-2 w-full', isMobile ? 'items-center' : 'items-start')}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div className="w-full max-w-[600px]">
            {tooltipNode}
            {infoBar}
          </div>
        </ClickAwayListener>

        {showAddButton && (
          // Mobile : pas de largeur fixe (w-72 laissait un grand vide autour du
          // bouton) — le selecteur prend la place, le bouton reste a sa taille
          <div className={clsx(isMobile ? 'shrink-0' : 'w-72 md:mt-4')}>
            <ProductAddToBasket qtyCart={qty} product={product} list />
          </div>
        )}
      </div>

      <OfferSubscription
        dialogVersion={true}
        openDialog={showPremiumDialog}
        setOpenDialog={setShowPremiumDialog}
      />
    </>
  )
}

export default ProductQtyInput
